import React, { FC, useEffect, useId } from 'react';
import { ModalDialog } from 'components/ModalDialog/ModalDialog';
import SVGInline from 'components/SVGInline/SVGInline';
import { format } from 'date-fns';

import { withNoScroll } from 'hooks/withNoScroll/withNoScroll';
import { useDeviceManagement, getDeviceIcon } from 'hooks/useDeviceManagement/useDeviceManagement';
import translate from 'utils/fnTranslate';
import icons from 'style';
import { AlertDialog } from 'components/AlertDialog/AlertDialog';
import OptionFocusable from 'components/Pages/Settings/OptionFocusable';
import { SignOutButton, Icon } from 'components/Pages/DevicePageCommon.css';
import DRM from 'providers/player/DRM';
import { useAuth } from 'providers/useAuth/AuthContext';
import {
    ModalOverlay,
    ModalContent,
    ModalTitle,
    ModalBodyText,
    DeviceListContainer,
    ButtonContainer,
    ModalButton,
    LoadingText,
    ErrorText,
} from './DeviceManagerModal.css';

type DeviceManagerModalProps = {
    onClose: () => void;
    onDeviceRemoved: () => void;
};

const DeviceManagerModalInner: FC<DeviceManagerModalProps> = ({ onClose, onDeviceRemoved }) => {
    const titleId = useId();
    const descriptionId = useId();
    const { logUserOut } = useAuth();

    const {
        devices,
        fetchLoading,
        fetchError,
        fetchDevices,
        deviceToRemove,
        setDeviceToRemove,
        removeDevice,
        removeError,
        removeResponseCode,
    } = useDeviceManagement(true);

    useEffect(() => {
        if (removeResponseCode?.toString().startsWith('2')) {
            const removedCurrentDevice = !!deviceToRemove && !deviceToRemove.bySessionId && deviceToRemove.id === DRM.getDeviceId();
            setDeviceToRemove(null);
            if (removedCurrentDevice) {
                window.history.replaceState(null, '', BASENAME ? `/${BASENAME}` : '/');
                logUserOut();
                return;
            }
            onDeviceRemoved();
        }
    }, [removeResponseCode]);

    return (
        <ModalDialog as={ModalOverlay} onClose={onClose} aria-labelledby={titleId} aria-describedby={descriptionId}>
            <ModalContent>
                <ModalTitle id={titleId}>{translate('ERR_DRM_TOO_MANY_DEVICES_TITLE')}</ModalTitle>
                <ModalBodyText id={descriptionId}>{translate('ERR_DRM_TOO_MANY_DEVICES_HINT')}</ModalBodyText>

                {fetchLoading && <LoadingText>...</LoadingText>}

                {fetchError && (
                    <>
                        <ErrorText>{translate('SETTINGS_ERROR_HINT')}</ErrorText>
                        <ButtonContainer>
                            <ModalButton onClick={fetchDevices} isPrimary label={translate('TRY_AGAIN_BUTTON')} link={null} />
                        </ButtonContainer>
                    </>
                )}

                {!fetchLoading && !fetchError && devices.length > 0 && (
                    <DeviceListContainer>
                        {[...devices]
                            .sort((a, b) => b.last_activity - a.last_activity)
                            .map((device, index) => {
                                const isDeletable = device.is_removable || !device.device_uuid;
                                const isCurrentDevice = device.device_uuid && device.device_uuid === DRM.getDeviceId();

                                return (
                                    <OptionFocusable
                                        key={`device-${index}`}
                                        title={device.displayed_device_name || `${device.os} ${device.os_version}`}
                                        subtitle={
                                            isCurrentDevice
                                                ? translate('DEVICE_MANAGEMENT_THIS_DEVICE')
                                                : `${translate('SETTINGS_DEVICE_LAST_USED')}: ${format(new Date(device.last_activity * 1000), 'dd.MM.yyyy, HH:mm')}`
                                        }
                                        icon={getDeviceIcon(device.screen_type, device.device_type, device.device_family)}
                                        role={isDeletable ? 'button' : undefined}
                                        onClick={
                                            isDeletable
                                                ? () =>
                                                      setDeviceToRemove(
                                                          device.device_uuid
                                                              ? { id: device.device_uuid, bySessionId: false }
                                                              : { id: device.id, bySessionId: true }
                                                      )
                                                : undefined
                                        }
                                        actionComponent={
                                            isDeletable ? (
                                                <SignOutButton aria-hidden>
                                                    <Icon>
                                                        <SVGInline svg={icons.icoTrash} />
                                                    </Icon>
                                                </SignOutButton>
                                            ) : (
                                                <></>
                                            )
                                        }
                                        ariaHint={isDeletable ? translate('ARIA_HINT_DEVICE_SIGNOUT') : undefined}
                                    />
                                );
                            })}
                    </DeviceListContainer>
                )}

                <ButtonContainer>
                    <ModalButton onClick={onClose} isPrimary={false} label={translate('CLOSE_BUTTON')} link={null} />
                </ButtonContainer>
            </ModalContent>

            {deviceToRemove && (
                <AlertDialog
                    title={translate(
                        removeError
                            ? 'SCREEN_DEVICE_MANAGEMENT_SIGNOUT_FAILED_HEADER'
                            : 'SCREEN_DEVICE_MANAGEMENT_DELETE_CONFIRMATION_HEADER'
                    )}
                    bodyText={translate(
                        removeError ? 'SCREEN_DEVICE_MANAGEMENT_SIGNOUT_FAILED_HINT' : 'SCREEN_DEVICE_MANAGEMENT_DELETE_CONFIRMATION_HINT'
                    )}
                    buttons={[
                        {
                            text: translate('CANCEL_BUTTON'),
                            onClick: () => setDeviceToRemove(null),
                        },
                        {
                            text: translate(removeError ? 'TRY_AGAIN_BUTTON' : 'SIGN_OUT_DEVICE_BUTTON'),
                            onClick: () => removeDevice(deviceToRemove),
                        },
                    ]}
                />
            )}
        </ModalDialog>
    );
};

export const DeviceManagerModal = withNoScroll(DeviceManagerModalInner);
